import { Dialog, DialogContent, DialogTitle, Fade, IconButton, Typography, withStyles } from '@material-ui/core';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import FileCopyIcon from '@material-ui/icons/FileCopy';

const styles = {
  paper: {
    width: 500,
    height: 310,
    padding: 16
  },
  codeContainer: {
    position: 'relative',
  },
  copyIcon: {
    position: 'absolute',
    top: 8,
    right: 8
  },
  code: {
    padding: 8,
    whiteSpace: 'pre-wrap'
  }
} as const;

interface Props {
  children?: ReactNode;
  classes: {[K in keyof typeof styles]: string}
}

interface State {
  error: Error | null,
  codeHover: boolean
}

const PageHasBeenForceRefreshed = 'page-has-been-force-refreshed';

const retryPageLoading = () => {
  const pageHasAlreadyBeenForceRefreshed = JSON.parse(
    window.localStorage.getItem(PageHasBeenForceRefreshed) || 'false'
  ) as boolean;

  if (!pageHasAlreadyBeenForceRefreshed) {
    window.localStorage.setItem(PageHasBeenForceRefreshed, 'true');
    return window.location.reload();
  } else {
    window.localStorage.setItem(PageHasBeenForceRefreshed, 'false');
  }
};
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    error: null,
    codeHover: false
  };

  public static getDerivedStateFromError(e: Error): State {
    // Update state so the next render will show the fallback UI.
    if (/Loading chunk [\d]+ failed/.test(e.message)) {
      retryPageLoading();
      return {
        error: null,
        codeHover: false
      };
    }
    return { error: e, codeHover: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  copyText = () => {
    const el = document.getElementById('error-trace')
    // Copy text
    navigator.clipboard.writeText(el.innerText);
    // Select text to indicate text was copied to user
    const range = document.createRange();
    range.selectNodeContents(el);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sel = window.getSelection()!;
    sel.removeAllRanges();
    sel.addRange(range);
  }

  public render() {
    if (this.state.error) {
      return <Dialog
        classes={{
          paper: this.props.classes.paper
        }}
        open
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>
              A browser error has occurred. Reopen the dashboard in order to continue working and open a <a href='https://rguroo.com/support'>support ticket</a> containing the following information:
          </Typography>
          <div
            className={this.props.classes.codeContainer}
            onMouseEnter={() => this.setState({codeHover: true})}
            onMouseLeave={() => this.setState({codeHover: false})}
          >
            <Fade in={this.state.codeHover}>
              <IconButton
                className={this.props.classes.copyIcon}
                onClick={this.copyText}>
                <FileCopyIcon/>
              </IconButton>
            </Fade>
            <pre
              className={this.props.classes.code}
              id='error-trace'
            >{`
Dashboard browser error:
${this.state.error.name} - ${this.state.error.message}
${this.state.error.stack}
              `}
            </pre>
          </div>
        </DialogContent>
      </Dialog>;
    }

    return this.props.children;
  }
}

export default withStyles(styles)(ErrorBoundary);